/**
 * Image Uploader component with camera support
 */

import * as React from 'react';
import {
  ImageUploader,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  cn,
} from '@frameit/ui';
import { logger } from '../../lib/logger';

export interface UploaderProps {
  /** Upload handler */
  onUpload: (file: File, dimensions: { width: number; height: number }) => void;
  /** Custom class name */
  className?: string;
  /** Allow camera capture */
  allowCamera?: boolean;
}

/** Uploader component */
export function Uploader({ onUpload, className, allowCamera = true }: UploaderProps) {
  const [showCamera, setShowCamera] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);

  // Start camera
  const startCamera = async () => {
    try {
      // First show the dialog so the video element is mounted
      setShowCamera(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;

      // Wait a tick for the video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // Explicitly play on mobile
          videoRef.current.play().catch((err) => {
            logger.error('Video play failed:', err);
          });
        }
      }, 100);
    } catch (error) {
      logger.error('Camera access denied:', error);
      setShowCamera(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(videoRef.current, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `capture-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          onUpload(file, { width: canvas.width, height: canvas.height });
          stopCamera();
        }
      },
      'image/jpeg',
      0.9,
    );
  };

  // Handle file upload
  const handleUpload = async (files: File[]) => {
    for (const file of files) {
      // Get image dimensions
      const dimensions = await getImageDimensions(file);
      onUpload(file, dimensions);
    }
  };

  // Get image dimensions
  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  // Cleanup camera on unmount
  React.useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return (
    <div className={cn('space-y-4', className)}>
      <ImageUploader onUpload={handleUpload} />

      {allowCamera && (
        <>
          <div className="flex items-center gap-4">
            <div className="flex-1 border-t" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 border-t" />
          </div>

          <Button variant="outline" className="w-full" onClick={startCamera}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Take Photo
          </Button>

          {/* Camera dialog */}
          <Dialog open={showCamera} onOpenChange={(open) => !open && stopCamera()}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Take Photo</DialogTitle>
              </DialogHeader>

              <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  onLoadedMetadata={(e) => {
                    // Ensure video plays on mobile after metadata loads
                    const video = e.currentTarget;
                    video.play().catch((err) => {
                      logger.error('Video play failed:', err);
                    });
                  }}
                />
              </div>

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={stopCamera}>
                  Cancel
                </Button>
                <Button onClick={capturePhoto}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
